'use client'

import React from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { ChevronDownIcon, XIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MultiSelectFilterProps {
  values: string[]
  onValuesChange: (values: string[]) => void
  items: { title: string; value: string }[]
  placeholder: string
}

function MultiSelectFilter({ values, onValuesChange, items, placeholder }: MultiSelectFilterProps) {
  const isMobile = useIsMobile()
  const [open, setOpen] = React.useState(false)

  const handleToggle = (value: string) => {
    if (values.includes(value)) {
      onValuesChange(values.filter((v) => v !== value))
    } else {
      onValuesChange([...values, value])
    }
  }

  const handleClear = () => {
    onValuesChange([])
  }

  const triggerLabel =
    values.length === 0
      ? placeholder
      : values.length === 1
        ? (items.find((i) => i.value === values[0])?.title ?? placeholder)
        : `${placeholder} (${values.length})`

  const trigger = (
    <button
      className={cn(
        'border-secondary rounded-full text-foreground text-base px-4 flex items-center justify-between gap-2 border bg-transparent py-2 whitespace-nowrap shadow-xs transition-[color,box-shadow] outline-none h-9',
        values.length === 0 && 'text-muted-foreground',
      )}
    >
      <span className='line-clamp-1'>{triggerLabel}</span>
      <ChevronDownIcon className='size-4 opacity-50' />
    </button>
  )

  const list = (
    <div className='flex flex-col'>
      {values.length > 0 && (
        <button
          onClick={handleClear}
          className='flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors'
        >
          <XIcon className='size-3.5' />
          Clear
        </button>
      )}
      <div className='max-h-64 overflow-y-auto'>
        {items.length === 0 ? (
          <p className='px-3 py-2 text-sm text-muted-foreground'>No available filters</p>
        ) : (
          items.map((item) => (
            <label
              key={item.value}
              className='flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-accent rounded-sm'
            >
              <Checkbox
                checked={values.includes(item.value)}
                onCheckedChange={() => handleToggle(item.value)}
              />
              <span>{item.title}</span>
            </label>
          ))
        )}
      </div>
    </div>
  )

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={setOpen}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{placeholder}</DrawerTitle>
          </DrawerHeader>
          <div className='px-4 pb-2'>{list}</div>
          <DrawerFooter>
            <DrawerClose asChild>
              <Button variant='outline'>Done</Button>
            </DrawerClose>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent align='start' className='w-56 p-1'>
        {list}
      </PopoverContent>
    </Popover>
  )
}

export default MultiSelectFilter
